// src/pages/Dashboard/teacher/paperEvaluation/PaperEvaluation.jsx
import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Wifi, WifiOff } from "lucide-react";
import PaperEvaluationSection from "./PaperEvaluationSection";
import PaperEvaluationModal from "./PaperEvaluationModal";
import {
  WS_URL,
  generateUploadUrls,
  stitchOCRText,
  evaluateAnswerApi,
} from "./paperEvaluationHelpers";
import { useTranslation } from "react-i18next";

// Simple ObjectId generator (24-char hex)
function generateObjectId() {
  const hex = "0123456789abcdef";
  let id = "";
  for (let i = 0; i < 24; i++) id += hex[Math.floor(Math.random() * 16)];
  return id;
}

const PaperEvaluation = () => {
  const { t } = useTranslation();
  const { user } = useSelector((state) => state.profile || {});

  const [teacherId, setTeacherId] = useState("");
  const [sessionId, setSessionId] = useState("");
  const [studentId, setStudentId] = useState("");
  const [subject, setSubject] = useState("");
  const [wsConnected, setWsConnected] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [ocrResults, setOcrResults] = useState([]);

  const [sections, setSections] = useState([
    { section_type: "SAQ", questions: [{ question_id: "Q1", files: [] }] },
    { section_type: "LAQ", questions: [{ question_id: "Q1", files: [] }] },
  ]);

  const [showRubricModal, setShowRubricModal] = useState(false);
  const [activeQuestionMeta, setActiveQuestionMeta] = useState(null);
  const [activeOcrText, setActiveOcrText] = useState("");
  const [evaluationResults, setEvaluationResults] = useState({});
  const [isEvaluating, setIsEvaluating] = useState(false);

  const wsRef = useRef(null);

  useEffect(() => {
    if (user && user._id) setTeacherId(user._id);
  }, [user]);

  // WebSocket Setup
  useEffect(() => {
    if (!teacherId) return;

    const connectWebSocket = () => {
      const ws = new WebSocket(`${WS_URL}/ws/${teacherId}`);

      ws.onopen = () => setWsConnected(true);
      ws.onerror = () => setWsConnected(false);
      ws.onclose = () => {
        setWsConnected(false);
        setTimeout(connectWebSocket, 3000);
      };

      ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          if (message.type === "OCR_COMPLETE") {
            setOcrResults((prev) => [...prev, message]);
            const key = `${message.student_id}_${message.section}_${message.question_id}_${message.page_index}`;
            setUploadProgress((prev) => ({
              ...prev,
              [key]: { status: "ocr_complete", text: message.text },
            }));
          }
        } catch (err) {
          console.error("WS error:", err);
        }
      };

      wsRef.current = ws;
    };

    connectWebSocket();
    return () => wsRef.current?.close();
  }, [teacherId]);

  // Upload helpers
  const handleFileSelect = (sectionIndex, questionIndex, newFiles) => {
    if (!newFiles?.length) return;
    setSections((prev) => {
      const newSections = JSON.parse(JSON.stringify(prev));
      const existing = prev[sectionIndex].questions[questionIndex].files || [];
      newSections[sectionIndex].questions[questionIndex].files = [
        ...existing,
        ...Array.from(newFiles),
      ];
      return newSections;
    });
  };

  const handleRemoveFile = (sectionIndex, questionIndex, fileIndex) => {
    setSections((prev) => {
      const newSections = [...prev];
      newSections[sectionIndex].questions[questionIndex].files.splice(
        fileIndex,
        1
      );
      return newSections;
    });
  };

  const handleAddQuestion = (sectionIndex) => {
    setSections((prev) => {
      const newSections = [...prev];
      const count = newSections[sectionIndex].questions.length;
      newSections[sectionIndex].questions.push({
        question_id: `Q${count + 1}`,
        files: [],
      });
      return newSections;
    });
  };

  const handleRemoveQuestion = (sectionIndex, questionIndex) => {
    setSections((prev) => {
      const ns = [...prev];
      if (ns[sectionIndex].questions.length > 1) {
        ns[sectionIndex].questions.splice(questionIndex, 1);
        ns[sectionIndex].questions = ns[sectionIndex].questions.map((q, idx) => ({
          ...q,
          question_id: `Q${idx + 1}`,
        }));
      }
      return ns;
    });
  };

  const uploadToS3 = async (file, presignedUrl, key) => {
    try {
      setUploadProgress((p) => ({
        ...p,
        [key]: { status: "uploading", progress: 0 },
      }));

      const res = await fetch(presignedUrl, {
        method: "PUT",
        body: file,
        headers: { "Content-Type": "image/jpeg" },
      });

      if (!res.ok) throw new Error("Upload failed");

      setUploadProgress((p) => ({
        ...p,
        [key]: { status: "uploaded", progress: 100 },
      }));

      return true;
    } catch (err) {
      setUploadProgress((p) => ({
        ...p,
        [key]: { status: "error", error: err.message },
      }));
      return false;
    }
  };

  const handleSubmit = async () => {
    if (!teacherId)
      return alert(t("paperEval.alertMissingTeacher"));

    if (!sessionId || !studentId || !subject)
      return alert(t("paperEval.alertMissingFields"));

    const hasFiles = sections.some((s) =>
      s.questions.some((q) => q.files.length > 0)
    );
    if (!hasFiles) return alert(t("paperEval.alertNoFiles"));

    try {
      const data = await generateUploadUrls({
        sessionId,
        teacherId,
        studentId,
        subject,
        sections,
      });

      const uploadPromises = [];
      sections.forEach((section) =>
        section.questions.forEach((q) => {
          const urls = data.urls[section.section_type][q.question_id];
          q.files.forEach((file, fileIndex) => {
            const key = `${studentId}_${section.section_type}_${q.question_id}_${fileIndex}`;
            uploadPromises.push(uploadToS3(file, urls[fileIndex], key));
          });
        })
      );

      await Promise.all(uploadPromises);

      alert(t("paperEval.alertUploadSuccess"));
    } catch (err) {
      alert(t("paperEval.alertError") + err.message);
    }
  };

  // Rubric Evaluation
  const openVerifyRubricModal = (sectionIndex, questionIndex) => {
    const section = sections[sectionIndex];
    const question = section.questions[questionIndex];

    setActiveQuestionMeta({
      sectionIndex,
      questionIndex,
      section_type: section.section_type,
      question_id: question.question_id,
    });

    setActiveOcrText(
      stitchOCRText(ocrResults, section.section_type, question.question_id)
    );

    setShowRubricModal(true);
  };

  const handleEvaluationSubmit = async ({
    questionText,
    maxMarks,
    keywords,
  }) => {
    if (!activeQuestionMeta) return;
    if (!sessionId) return alert(t("paperEval.alertMissingSessionId"));

    const { section_type, question_id } = activeQuestionMeta;
    const key = `${section_type}_${question_id}`;

    const payload = {
      id: sessionId,
      question: questionText,
      keywords,
      marks: maxMarks,
      question_type: section_type,
      question_number: question_id,
    };

    try {
      setIsEvaluating(true);
      const result = await evaluateAnswerApi(payload);
      setEvaluationResults((prev) => ({ ...prev, [key]: result }));
      setShowRubricModal(false);
    } catch (err) {
      alert(t("paperEval.alertEvalError") + err.message);
    } finally {
      setIsEvaluating(false);
    }
  };

  const totalImages = sections.reduce(
    (sum, s) => sum + s.questions.reduce((t, q) => t + q.files.length, 0),
    0
  );

  return (
    <div className="min-h-screen bg-gray-50 p-4 sm:p-8">
      <div className="max-w-6xl mx-auto">

        {/* HEADER */}
        <div className="bg-white rounded-xl shadow-md p-6 mb-6 border border-gray-100">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
                {t("paperEval.title")}
              </h1>
              <p className="text-sm text-gray-500">
                {t("paperEval.subtitle")}
              </p>
            </div>

            <div className="flex items-center gap-2 px-3 py-1.5 rounded-full border border-gray-200 bg-gray-50">
              {wsConnected ? (
                <>
                  <span className="h-2 w-2 rounded-full bg-green-500" />
                  <Wifi size={18} className="text-green-500" />
                  <span className="text-xs text-green-700">
                    {t("paperEval.wsConnected")}
                  </span>
                </>
              ) : (
                <>
                  <span className="h-2 w-2 rounded-full bg-red-500" />
                  <WifiOff size={18} className="text-red-500" />
                  <span className="text-xs text-red-700">
                    {t("paperEval.wsDisconnected")}
                  </span>
                </>
              )}
            </div>
          </div>

          {/* BASIC INFO */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">

            {/* Teacher ID */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t("paperEval.teacherId")}
              </label>
              <input
                value={teacherId}
                onChange={(e) => setTeacherId(e.target.value)}
                className="w-full px-3 py-2 border rounded-md text-sm"
              />
              <p className="text-xs text-gray-400">
                {t("paperEval.teacherHelp")}
              </p>
            </div>

            {/* Session ID */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t("paperEval.sessionId")}
              </label>
              <div className="flex gap-2">
                <input
                  value={sessionId}
                  onChange={(e) => setSessionId(e.target.value)}
                  placeholder={t("paperEval.genPlaceholder")}
                  className="w-full px-3 py-2 border rounded-md text-sm"
                />
                <button
                  onClick={() => setSessionId(generateObjectId())}
                  className="px-3 py-2 bg-blue-600 text-white rounded-md text-xs hover:bg-blue-700"
                >
                  {t("paperEval.generate")}
                </button>
              </div>
              <p className="text-xs text-gray-400">
                {t("paperEval.sessionHelp")}
              </p>
            </div>

            {/* Student ID */}
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1">
                {t("paperEval.studentId")}
              </label>
              <input
                value={studentId}
                onChange={(e) => setStudentId(e.target.value)}
                placeholder={t("paperEval.studentPlaceholder")}
                className="w-full px-3 py-2 border rounded-md text-sm"
              />
            </div>

            {/* Subject */}
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1">
                {t("paperEval.subject")}
              </label>
              <input
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder={t("paperEval.subjectPlaceholder")}
                className="w-full px-3 py-2 border rounded-md text-sm"
              />
            </div>
          </div>
        </div>

        {/* Sections */}
        {sections.map((section, index) => (
          <PaperEvaluationSection
            key={index}
            section={section}
            sectionIndex={index}
            studentId={studentId}
            uploadProgress={uploadProgress}
            evaluationResults={evaluationResults}
            onAddQuestion={handleAddQuestion}
            onRemoveQuestion={handleRemoveQuestion}
            onFileSelect={handleFileSelect}
            onRemoveFile={handleRemoveFile}
            onOpenVerifyModal={openVerifyRubricModal}
          />
        ))}

        {/* Upload Button */}
        <div className="bg-white p-6 rounded-xl shadow-md border mt-4">
          <button
            onClick={handleSubmit}
            disabled={!sessionId || !studentId || !subject}
            className="w-full bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 disabled:bg-gray-400"
          >
            {t("paperEval.uploadProcess")}
          </button>
          <p className="text-xs text-gray-500 text-center mt-2">
            {t("paperEval.totalImages")} {totalImages}
          </p>
        </div>

        {/* OCR Results */}
        {ocrResults.length > 0 && (
          <div className="bg-white rounded-xl shadow-md p-6 mt-6 border">
            <h2 className="text-lg font-semibold mb-4">
              {t("paperEval.ocrResults")}
            </h2>

            <div className="space-y-3">
              {ocrResults.map((r, i) => (
                <div key={i} className="p-3 bg-green-50 border rounded-lg">
                  <div className="flex justify-between text-sm">
                    <span>
                      {r.section} • {r.question_id} • {t("paperEval.page")} {r.page_index + 1}
                    </span>
                    <span className="text-green-700 flex items-center gap-1 text-xs">
                      <Wifi size={14} />
                      {t("paperEval.live")}
                    </span>
                  </div>

                  <div className="mt-2 p-3 bg-white border rounded text-xs">
                    {r.text.substring(0, 300)}
                    {r.text.length > 300 && "..."}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <PaperEvaluationModal
        isOpen={showRubricModal}
        onClose={() => setShowRubricModal(false)}
        onSubmit={handleEvaluationSubmit}
        ocrText={activeOcrText}
        questionMeta={activeQuestionMeta}
        isSubmitting={isEvaluating}
      />
    </div>
  );
};

export default PaperEvaluation;
